import { CURRENT_SERIES_ID } from '@/config/app.config';
import { read, write } from './http';
import {
  demoTeam,
  demoTeamSummaries,
  replaceDemoTeam,
  setDemoArmband,
  swapInDemoTeam,
} from './mock/demo-state';
import { MOCK_CONTEST_TEAM, MOCK_ROSTER } from './mock/gameplay.mock';
import type { ContestEntry, ContestTeam, ContestTeamSummary, RosterPlayer } from '@/types';

/**
 * Contest gameplay.
 *
 * Every write returns the team as the server now sees it, so callers replace
 * their copy rather than patching it — the API is authoritative on transfer
 * counts and on whether the window is still open.
 */

/**
 * GET /api/v1/me/teams — newest entry first.
 *
 * Teams in finished contests are left out unless `includePast` is set.
 */
export function getMyTeams(
  signal?: AbortSignal,
  includePast = false,
): Promise<readonly ContestTeamSummary[]> {
  return read(includePast ? '/me/teams?includePast=true' : '/me/teams', demoTeamSummaries, signal);
}

/** GET /api/v1/me/teams/:teamId/current — full player detail plus window state. */
export function getCurrentTeam(teamId: string, signal?: AbortSignal): Promise<ContestTeam> {
  return read(`/me/teams/${teamId}/current`, () => demoTeam() ?? MOCK_CONTEST_TEAM, signal);
}

/** True while the manager can still change this team right now. */
export const isTeamEditable = (team: ContestTeam): boolean => team.canEdit && !team.transfersBlocked;

/**
 * Which of several teams "my team" means, when no id is given.
 *
 * The one that most needs attention: of the teams that can still be changed,
 * the one whose next lock comes soonest. If none can, the most recent entry.
 * Deterministic, so My Team and the dashboard always agree, and the same team
 * comes back on every visit.
 *
 * `teams` must be in the API's order — newest entry first.
 */
export function chooseDefaultTeam(teams: readonly ContestTeam[]): ContestTeam | null {
  const lockTime = (team: ContestTeam): number =>
    team.nextLockAt ? new Date(team.nextLockAt).getTime() : Number.POSITIVE_INFINITY;

  const editable = teams
    .filter(isTeamEditable)
    .sort((a, b) => lockTime(a) - lockTime(b));

  return editable[0] ?? teams[0] ?? null;
}

/**
 * The manager's default team, resolved without the caller knowing an id.
 *
 * Screens show "my team" rather than a specific contest entry, and the API has
 * no default-team route, so every current team is loaded and one chosen by
 * `chooseDefaultTeam`. With one team that is simply it.
 */
export async function getPrimaryTeam(signal?: AbortSignal): Promise<ContestTeam | null> {
  const teams = await getMyTeams(signal);
  if (teams.length === 0) return null;

  const detailed = await Promise.all(teams.map((team) => getCurrentTeam(team.id, signal)));
  return chooseDefaultTeam(detailed);
}

/**
 * PUT /api/v1/me/teams/:teamId
 *
 * Replace the whole team. Allowed only before the contest's first fixture has
 * been played, and free — no transfer is counted — so it is the way to change
 * your mind after entering rather than a second entry route. Once a fixture
 * settles the API refuses this with `EDIT_WINDOW_CLOSED` and transfers take
 * over.
 *
 * `teamName` is required here, unlike on entry, where the manager's saved name
 * is used as a fallback.
 */
export function replaceTeam(
  teamId: string,
  entry: ContestEntry & { teamName: string },
): Promise<ContestTeam> {
  return write(`/me/teams/${teamId}`, 'PUT', entry, () => replaceDemoTeam(entry));
}

/**
 * POST /api/v1/me/teams/:teamId/transfers
 *
 * A lineup swap, not a market transfer: `outPlayerId` must be a starter and
 * drops to the bench, `inPlayerId` must be on the bench and joins the eleven.
 * The captain and vice cannot be dropped — move the armband first.
 *
 * Free until the contest's first fixture locks; after that each swap spends one
 * transfer from the contest's cap. The API also checks the eleven still covers
 * every position, and refuses once the window closes.
 */
export function applyTransfer(
  teamId: string,
  outPlayerId: number,
  inPlayerId: number,
): Promise<ContestTeam> {
  return write(
    `/me/teams/${teamId}/transfers`,
    'POST',
    { outPlayerId, inPlayerId },
    () => swapInDemoTeam(outPlayerId, inPlayerId) ?? MOCK_CONTEST_TEAM,
  );
}

/** PATCH /api/v1/me/teams/:teamId/armband — captain and vice are set together. */
export function setArmband(
  teamId: string,
  captainPlayerId: number,
  vicePlayerId: number,
): Promise<ContestTeam> {
  return write(
    `/me/teams/${teamId}/armband`,
    'PATCH',
    { captainPlayerId, vicePlayerId },
    () => setDemoArmband(captainPlayerId, vicePlayerId) ?? MOCK_CONTEST_TEAM,
  );
}

/**
 * GET /api/v1/football/series/:seriesId/players
 *
 * The pool a squad is picked from. Returns raw roster rows — no price and no
 * fantasy points, so the pool can be browsed but not priced.
 */
export function getRoster(
  query: { search?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<readonly RosterPlayer[]> {
  const params = new URLSearchParams();
  if (query.search?.trim()) params.set('search', query.search.trim());
  params.set('limit', String(query.limit ?? 100));

  return read(
    `/football/series/${CURRENT_SERIES_ID}/players?${params.toString()}`,
    () => MOCK_ROSTER,
    signal,
  );
}
